/**
 * Regras dos anexos do chat da discussão — o que o composer aceita antes de
 * mandar para `POST /discussoes/anexos`.
 *
 * ⚠️ Espelho do perfil `discussao-anexo` do backend (`src/infra/storage/perfis.ts`):
 * mesmos formatos e mesmo limite. O servidor continua sendo quem decide; checar
 * aqui só evita a pessoa descobrir, depois de enviar metade da fila, que o
 * quarto arquivo era grande demais.
 */
export const FORMATOS_DE_ANEXO = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp'] as const;

export const MAX_BYTES_ANEXO = 10 * 1024 * 1024;

/** Quantos arquivos cabem numa leva. Cada um vira uma mensagem. */
export const MAX_ANEXOS_POR_ENVIO = 10;

export interface AnexoRecusado {
  nome: string;
  motivo: string;
}

/** Mesmo arquivo escolhido duas vezes (pelo clipe e depois arrastado) não entra repetido. */
function mesmoArquivo(a: File, b: File): boolean {
  return a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;
}

/**
 * Soma `novos` aos `atuais`, separando o que não pode ir.
 *
 * Função pura de propósito: o composer só chama e mostra o resultado, e a regra
 * (formato, tamanho, repetido, limite da leva) se testa sem tela.
 */
export function adicionarAnexos(
  atuais: readonly File[],
  novos: Iterable<File>,
): { arquivos: File[]; recusados: AnexoRecusado[] } {
  const arquivos = [...atuais];
  const recusados: AnexoRecusado[] = [];

  for (const arquivo of novos) {
    if (!(FORMATOS_DE_ANEXO as readonly string[]).includes(arquivo.type)) {
      recusados.push({ nome: arquivo.name, motivo: 'formato não aceito (só PDF ou imagem)' });
    } else if (arquivo.size > MAX_BYTES_ANEXO) {
      recusados.push({ nome: arquivo.name, motivo: 'maior que 10 MB' });
    } else if (arquivos.some((a) => mesmoArquivo(a, arquivo))) {
      // Repetido: some sem aviso — a pessoa já vê o arquivo na lista.
    } else if (arquivos.length >= MAX_ANEXOS_POR_ENVIO) {
      recusados.push({ nome: arquivo.name, motivo: `limite de ${MAX_ANEXOS_POR_ENVIO} por envio` });
    } else {
      arquivos.push(arquivo);
    }
  }

  return { arquivos, recusados };
}

/** `true` quando o que está sendo arrastado traz arquivos (e não texto selecionado). */
export function arrastoTemArquivos(dados: DataTransfer | null): boolean {
  return Boolean(dados && Array.from(dados.types).includes('Files'));
}
